import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";

const DS = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)",
  surface: "#FFFFFF", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
};

export const TEMPLATE_DEFS = {
  registration_confirmation: {
    label: "Registration Confirmation",
    description: "Sent when a student successfully registers for a class",
    defaultSubject: "Registration Confirmed: {{courseName}}",
    defaultBodyIntro:
      "Your registration for {{courseName}} has been confirmed. We look forward to seeing you on {{sessionDate}}.",
    variables: [
      { token: "{{studentName}}", desc: "Student's full name" },
      { token: "{{courseName}}", desc: "Name of the course" },
      { token: "{{sessionDate}}", desc: "First session date" },
      { token: "{{sessionTime}}", desc: "Start time" },
      { token: "{{location}}", desc: "Class location" },
    ],
  },
  session_reminder: {
    label: "Session Reminder",
    description: "Sent the day before an upcoming class session",
    defaultSubject: "Reminder: {{courseName}} tomorrow",
    defaultBodyIntro:
      "This is a reminder that your session for {{courseName}} is scheduled for tomorrow, {{sessionDate}} at {{sessionTime}}.",
    variables: [
      { token: "{{studentName}}", desc: "Student's full name" },
      { token: "{{courseName}}", desc: "Name of the course" },
      { token: "{{sessionDate}}", desc: "Session date" },
      { token: "{{sessionTime}}", desc: "Start time" },
      { token: "{{location}}", desc: "Session location" },
      { token: "{{instructorName}}", desc: "Instructor's name" },
    ],
  },
  cert_expiry_warning: {
    label: "Certification Expiry Warning",
    description: "Sent at 60, 30, and 7 days before a certification expires",
    defaultSubject: "Action Required: {{certName}} expires in {{daysUntilExpiry}} days",
    defaultBodyIntro:
      "Your {{certName}} certification will expire on {{expiryDate}}. Please take action to renew it.",
    variables: [
      { token: "{{studentName}}", desc: "Holder's full name" },
      { token: "{{certName}}", desc: "Certification name" },
      { token: "{{expiryDate}}", desc: "Expiry date" },
      { token: "{{daysUntilExpiry}}", desc: "Days until expiry" },
    ],
  },
  new_submission: {
    label: "New Course Proposal",
    description: "Sent to coordinators when an instructor submits a course proposal",
    defaultSubject: "New Course Submission: {{courseTitle}}",
    defaultBodyIntro:
      "{{instructorName}} submitted {{courseTitle}} for coordinator review.",
    variables: [
      { token: "{{courseTitle}}", desc: "Submitted course title" },
      { token: "{{instructorName}}", desc: "Instructor's name" },
      { token: "{{category}}", desc: "Course category" },
      { token: "{{hours}}", desc: "Total instructional hours" },
    ],
  },
  instructor_submission_received: {
    label: "Proposal Received",
    description: "Sent to instructors after their course proposal is received",
    defaultSubject: "Proposal Received: {{courseTitle}}",
    defaultBodyIntro:
      "Your course proposal for {{courseTitle}} has been received and is waiting for coordinator review.",
    variables: [
      { token: "{{courseTitle}}", desc: "Submitted course title" },
      { token: "{{category}}", desc: "Course category" },
      { token: "{{hours}}", desc: "Total instructional hours" },
    ],
  },
  proposal_decision: {
    label: "Proposal Decision",
    description: "Sent to the instructor when their course proposal is approved or rejected",
    defaultSubject: "Course Proposal Update: {{courseTitle}}",
    defaultBodyIntro: "",
    variables: [
      { token: "{{instructorName}}", desc: "Instructor's name" },
      { token: "{{courseTitle}}", desc: "Proposed course title" },
      { token: "{{status}}", desc: "Decision outcome (Approved / Not Approved)" },
    ],
  },
  new_session_request: {
    label: "New Session Request",
    description: "Sent to coordinators when an instructor requests a new course session",
    defaultSubject: "New session request from {{instructorName}} for {{classTitle}}",
    defaultBodyIntro:
      "{{instructorName}} requested a new {{sessionType}} session for {{classTitle}}.",
    variables: [
      { token: "{{instructorName}}", desc: "Instructor's name" },
      { token: "{{classTitle}}", desc: "Course title" },
      { token: "{{sessionDate}}", desc: "Requested session date" },
      { token: "{{sessionType}}", desc: "Physical or online session type" },
      { token: "{{location}}", desc: "Location or meeting link" },
    ],
  },
  session_request_decision: {
    label: "Session Request Decision",
    description: "Sent to instructors when a requested session is approved or rejected",
    defaultSubject: "Session request update: {{classTitle}}",
    defaultBodyIntro: "",
    variables: [
      { token: "{{classTitle}}", desc: "Course title" },
      { token: "{{sessionDate}}", desc: "Requested session date" },
      { token: "{{badgeLabel}}", desc: "Decision outcome" },
    ],
  },
  instructor_signup_pending: {
    label: "Instructor Slot Sign-up",
    description: "Sent to coordinators when an instructor signs up for a session slot that needs approval",
    defaultSubject: "Approve instructor sign-up: {{instructorName}} for {{courseTitle}}",
    defaultBodyIntro: "",
    variables: [
      { token: "{{instructorName}}", desc: "Instructor who signed up" },
      { token: "{{courseTitle}}", desc: "Course number and title" },
      { token: "{{sessionDate}}", desc: "Session date" },
      { token: "{{sessionTime}}", desc: "Session start and end time" },
      { token: "{{teachingRole}}", desc: "Slot role (Lead Instructor, TA, ...)" },
      { token: "{{location}}", desc: "Session location" },
      { token: "{{signupSummary}}", desc: "Sign-ups awaiting approval, and the limit if set" },
    ],
  },
  instructor_signup_decision: {
    label: "Instructor Sign-up Decision",
    description: "Sent to an instructor when a coordinator approves or does not approve their slot sign-up",
    defaultSubject: "Sign-up approved: {{courseTitle}}",
    defaultBodyIntro: "",
    variables: [
      { token: "{{courseTitle}}", desc: "Course number and title" },
      { token: "{{sessionDate}}", desc: "Session date" },
      { token: "{{sessionTime}}", desc: "Session start and end time" },
      { token: "{{teachingRole}}", desc: "Slot role" },
      { token: "{{location}}", desc: "Session location" },
      { token: "{{badgeLabel}}", desc: "Decision outcome (Approved / Not Approved)" },
    ],
  },
  instructor_invite: {
    label: "Instructor Invite",
    description: "Sent when a coordinator invites a new instructor",
    defaultSubject: "You've been invited to teach at {{tenantName}}",
    defaultBodyIntro:
      "{{invitedByName}} invited you to join {{tenantName}} as an instructor.",
    variables: [
      { token: "{{invitedByName}}", desc: "Name of the person sending the invite" },
      { token: "{{tenantName}}", desc: "Training center name" },
      { token: "{{expiresAt}}", desc: "Invitation expiration date" },
      { token: "{{acceptUrl}}", desc: "Invitation acceptance link" },
    ],
  },
  welcome_new_student: {
    label: "Welcome — New Student",
    description: "Sent when a new student account is activated",
    defaultSubject: "Welcome to {{trainingCenterName}}",
    defaultBodyIntro:
      "Your account has been activated. Browse available courses and enroll in sessions that fit your schedule.",
    variables: [
      { token: "{{studentName}}", desc: "Student's full name" },
      { token: "{{trainingCenterName}}", desc: "Your training center name" },
      { token: "{{loginUrl}}", desc: "Link to the student portal" },
    ],
  },
  password_reset_request: {
    label: "Password Reset Request",
    description: "Sent when a user requests a password reset link",
    defaultSubject: "Reset your password",
    defaultBodyIntro: "",
    variables: [
      { token: "{{userName}}", desc: "Requesting user's full name" },
      { token: "{{resetUrl}}", desc: "Password reset link" },
    ],
  },
};

function humanize(templateId) {
  return templateId
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Templates without a hand-written definition fall back to what the backend registry knows about them.
function defFromEntry(entry) {
  if (!entry) return null;
  return {
    label: humanize(entry.templateId),
    description: "",
    defaultSubject: entry.subjectDefault ?? "",
    defaultBodyIntro: "",
    variables: (entry.requiredVars ?? []).map((v) => ({ token: `{{${v}}}`, desc: "" })),
  };
}

export default function EmailTemplateEditorDialog({
  open,
  templateId,
  entry,
  savedConfig,
  tenantId,
  userEmail,
  onClose,
  onSave,
  onTestSend,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tPrimary50 = alpha(tPrimary, 0.10);
  const tSecondary = theme.palette.secondary.main;
  const tSecondary50 = alpha(tSecondary, 0.10);
  const ctrlSx = useMemo(() => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: DS.surface2,
      "& fieldset": { borderWidth: "1.5px", borderColor: DS.line },
      "&:hover fieldset": { borderColor: "var(--color-hairline-cool)" },
      "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { fontSize: 14, fontWeight: 500, color: DS.ink },
  }), [tPrimary]);
  const def = useMemo(() => TEMPLATE_DEFS[templateId] ?? defFromEntry(entry), [templateId, entry]);

  const [tab, setTab] = useState(0);
  const [subject, setSubject] = useState("");
  const [bodyIntro, setBodyIntro] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(null);

  // Fields follow the saved override; the status message must outlive the save that updates it.
  useEffect(() => {
    if (open && def) {
      setSubject(savedConfig?.subject ?? "");
      setBodyIntro(savedConfig?.bodyIntro ?? "");
    }
  }, [open, templateId, savedConfig, def]);

  useEffect(() => {
    if (open) {
      setTestEmail(userEmail ?? "");
      setTab(0);
      setMsg(null);
    }
  }, [open, templateId, userEmail]);

  if (!def) return null;

  const effectiveSubject = subject.trim() || def.defaultSubject;
  const effectiveBodyIntro = bodyIntro.trim() || def.defaultBodyIntro;

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await onSave(templateId, { subject: subject.trim(), bodyIntro: bodyIntro.trim() });
      setMsg({ ok: true, text: "Template saved." });
    } catch {
      setMsg({ ok: false, text: "Save failed — please try again." });
    } finally {
      setSaving(false);
    }
  };

  const hasOverride = Boolean(savedConfig?.subject || savedConfig?.bodyIntro);

  const handleReset = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await onSave(templateId, { subject: "", bodyIntro: "" });
      setSubject("");
      setBodyIntro("");
      setMsg({ ok: true, text: "Reset to the default template." });
    } catch {
      setMsg({ ok: false, text: "Reset failed — please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) return;
    setTesting(true);
    setMsg(null);
    try {
      await onTestSend(templateId, testEmail.trim());
      setMsg({ ok: true, text: `Test email sent to ${testEmail.trim()}.` });
    } catch {
      setMsg({ ok: false, text: "Failed to send test email." });
    } finally {
      setTesting(false);
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(token);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          px: "24px", py: "18px",
          borderBottom: `1px solid ${DS.line2}`,
          display: "flex", alignItems: "flex-start", gap: "12px",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: DS.ink, letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>
            {def.label}
          </Typography>
          <Typography sx={{ fontSize: 13, color: DS.ink3, mt: "2px" }}>{def.description}</Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: DS.ink4, "&:hover": { bgcolor: DS.surface3 } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: `1px solid ${DS.line2}`, px: "24px" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            TabIndicatorProps={{ style: { backgroundColor: tPrimary, height: 2 } }}
          >
            <Tab
              label="Edit"
              sx={{
                fontSize: 13, fontWeight: 600, textTransform: "none",
                color: DS.ink3, "&.Mui-selected": { color: tPrimary },
              }}
            />
            <Tab
              label="Preview"
              sx={{
                fontSize: 13, fontWeight: 600, textTransform: "none",
                color: DS.ink3, "&.Mui-selected": { color: tPrimary },
              }}
            />
          </Tabs>
        </Box>

        {tab === 0 && (
          <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* Subject */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: DS.ink3, mb: "8px" }}>
                Subject Line
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={def.defaultSubject}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ "data-testid": "email-template-subject" }}
                sx={ctrlSx}
                helperText={!subject.trim() ? `Default: "${def.defaultSubject}"` : undefined}
              />
            </Box>

            {/* Body Intro */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: DS.ink3, mb: "8px" }}>
                Body Introduction
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                size="small"
                placeholder={def.defaultBodyIntro || "Enter a custom message shown at the top of the email…"}
                value={bodyIntro}
                onChange={(e) => setBodyIntro(e.target.value)}
                sx={ctrlSx}
                helperText={
                  !bodyIntro.trim() && def.defaultBodyIntro
                    ? `Default: "${def.defaultBodyIntro.slice(0, 80)}…"`
                    : undefined
                }
              />
            </Box>

            {/* Variable tokens */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: DS.ink3, mb: "10px" }}>
                Available Variables — click to copy
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {def.variables.map((v) => (
                  <Tooltip key={v.token} title={v.desc} placement="top">
                    <Chip
                      label={copied === v.token ? "Copied!" : v.token}
                      size="small"
                      onClick={() => copyToken(v.token)}
                      icon={
                        copied === v.token
                          ? <CheckCircleIcon sx={{ fontSize: 14, color: `${tSecondary} !important` }} />
                          : <ContentCopyIcon sx={{ fontSize: 13 }} />
                      }
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: copied === v.token ? tSecondary50 : tPrimary10,
                        color: copied === v.token ? tSecondary : tPrimary,
                        border: `1px solid ${copied === v.token ? alpha(tSecondary, 0.20) : alpha(tPrimary, 0.15)}`,
                        cursor: "pointer",
                        "& .MuiChip-icon": { color: "inherit" },
                        "&:hover": { bgcolor: tPrimary50 },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            {/* Send test email */}
            <Box sx={{ bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "12px", p: "16px" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: DS.ink, mb: "10px" }}>
                Send Test Email
              </Typography>
              <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  placeholder="recipient@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  sx={{ ...ctrlSx, flex: 1 }}
                />
                <Button
                  variant="outlined"
                  disabled={!testEmail.trim() || testing}
                  onClick={handleTest}
                  startIcon={
                    testing
                      ? <CircularProgress size={13} />
                      : <SendIcon sx={{ fontSize: 15 }} />
                  }
                  sx={{
                    height: 38, px: "16px", borderRadius: "10px",
                    border: `1.5px solid ${DS.line}`, color: DS.ink2,
                    textTransform: "none", fontWeight: 600, fontSize: 13,
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: DS.surface3, borderColor: "var(--color-hairline-cool)" },
                  }}
                >
                  {testing ? "Sending…" : "Send Test"}
                </Button>
              </Box>
            </Box>

            {msg && (
              <Alert severity={msg.ok ? "success" : "error"} sx={{ borderRadius: "10px" }}>
                {msg.text}
              </Alert>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: "24px" }}>
            <Box sx={{ p: "20px", bgcolor: "#e4eaf0", borderRadius: "12px", border: `1px solid ${DS.line}` }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.ink4, mb: "14px" }}>
                Email Preview
              </Typography>
              <Box sx={{ bgcolor: DS.surface, border: "1px solid #c5d2dc", borderRadius: "10px", overflow: "hidden", maxWidth: 480, mx: "auto" }}>
                <Box sx={{ height: 6, bgcolor: tPrimary }} />
                <Box sx={{ p: "20px 24px 14px" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: tPrimary }}>
                    Training Center
                  </Typography>
                  <Box sx={{ height: 1, bgcolor: DS.line, my: "12px" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: DS.ink, mb: "4px" }}>
                    Subject: {effectiveSubject}
                  </Typography>
                  {effectiveBodyIntro && (
                    <Typography sx={{ fontSize: 12.5, color: DS.ink2, mt: "8px", lineHeight: 1.6 }}>
                      {effectiveBodyIntro}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ bgcolor: "var(--color-surface-2)", px: "24px", py: "14px", borderTop: `1px solid ${DS.line}` }}>
                  <Typography sx={{ fontSize: 11, color: DS.ink3 }}>
                    (Email details — dates, course name, location — are populated at send time)
                  </Typography>
                </Box>
                <Box sx={{ px: "24px", py: "16px", textAlign: "center" }}>
                  <Box component="span" sx={{ display: "inline-block", bgcolor: tPrimary, color: "#fff", fontSize: 12.5, fontWeight: 700, px: "20px", py: "10px", borderRadius: "7px" }}>
                    View in Portal
                  </Box>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 11, color: DS.ink4, mt: "10px", textAlign: "center" }}>
                This is a structural preview. Send a test email for a full render.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: "24px", py: "16px",
          borderTop: `1px solid ${DS.line2}`,
          gap: "8px",
        }}
      >
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", color: DS.ink3, fontWeight: 600, fontSize: 13 }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {hasOverride && (
          <Button
            disabled={saving}
            onClick={handleReset}
            sx={{ textTransform: "none", color: DS.ink3, fontWeight: 600, fontSize: 13 }}
          >
            Reset to default
          </Button>
        )}
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSave}
          startIcon={
            saving
              ? <CircularProgress size={13} sx={{ color: "#fff" }} />
              : <SaveIcon sx={{ fontSize: 15 }} />
          }
          sx={{
            height: 38, px: "20px", borderRadius: "10px",
            bgcolor: tPrimary, textTransform: "none",
            fontWeight: 700, fontSize: 13,
            boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`,
            "&:hover": { bgcolor: tPrimaryDk },
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
